import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import RenderField from "./subComponents/RenderField";
import { useCreateProject } from "../../../../../queries/query-hooks/workspace/workspace-hook";
import * as Yup from "yup";
import { FieldArray, FormikProvider, useFormik } from "formik";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import { getPrincipleEntitiesForSelect } from "../../../../../common/utils/utils";
import postService from "../../../../../api/services/post-service";
import {
  ADD_PROJECT,
  GET_CONTENT_SEARCH_DATA,
  UPDATE_PROJECT,
} from "../../../../../api/endpoints/channel-endpoint";
import toast from "react-hot-toast";
import useBackdrop from "../../../../../components/custom-hooks/use-backdrop";
import { useQueryClient } from "@tanstack/react-query";
import { GET_PROJECT_POST_LIST } from "../../../../../queries/query-constants/workspace/workspace-constant";
import useRedirect from "../../../../../components/custom-hooks/use-redirect";
import FormBuilder from "./subComponents/FormBuilder";
import { findIcon } from "views/dashboard/icons";
import MappedStates from "./subComponents/MappedStates";
import MappedSpecialities from "./subComponents/MappedSpecialities";

const SponsoredCommunityForm = ({
  project_type_code,
  project_group_id,
  project_type_id,
  projectDetails = [],
  channelData = [],
  stateLists = [],
  masterSpecialities = [],
}) => {
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const { redirectTo } = useRedirect();
  const queryClient = useQueryClient();
  const { data: createProjectData, isLoading: principalEntitiesLoading } =
    useCreateProject();
  const [createdTags, setCreatedTags] = useState([]);
  const [searchText, setSearchText] = useState({});
  const [isEditProject, setIsEditProject] = useState(false);
  const [dropdownGroups, setDropdownGroups] = useState([
    { id: 1, firstSelect: "", secondSelect: "", isLocked: false },
  ]);
  const [specialitiesGroups, setSpecialitiesGroups] = useState([
    { id: 1, firstSelect: "", secondSelect: "", isLocked: false },
  ]);

  const getMappedData = (stateData = []) => {
    const mapped_data = {};
    stateData.forEach((item) => {
      if (item?.firstSelect && item?.secondSelect) {
        mapped_data[item.firstSelect] = item.secondSelect;
      }
    });
    return mapped_data;
  };

  const formik = useFormik({
    initialValues: {
      project_name: "",
      project_desc: "",
      principal_entity_id: "",
      project_attributes: "",
      project_start_date: "",
      project_end_date: "",
      project_owner: "",
      project_contact_name: "",
      project_contact_email: "",
      project_contact_phone: "",
      project_type_id: project_type_id,
      project_group_id: project_group_id,
      project_status: 1, // 1 - Active, 2 - Inactive
      project_po_ref: "",
      project_po_date: "",
      project_logo: "",
      project_impressions_target: "",
      show_speciality_info: 1, // 1 - Yes, 0 - No
      project_to_channel: [
        {
          type_label: "",
          channel_id: "",
          target_views: "",
          target_impression: "",
          target_engagement: "",
        },
      ],
    },
    validationSchema: Yup.object({
      project_name: Yup.string().trim().required("Project Name is required"),
      project_desc: Yup.string()
        .trim()
        .required("Project Description is required"),
      principal_entity_id: Yup.string().required(
        "Principal Entity is required"
      ),
      project_start_date: Yup.date()
        .required("Start date is required")
        .test(
          "start_date_before_end_date",
          "Start date must be before the End date.",
          function (value) {
            const { project_end_date } = this.parent;
            return (
              !project_end_date ||
              !value ||
              new Date(value) < new Date(project_end_date)
            );
          }
        ),
      project_end_date: Yup.date()
        .required("End date is required")
        .test(
          "end_date_after_start_date",
          "End date must be after the Start date.",
          function (value) {
            const { project_start_date } = this.parent;
            return (
              !project_start_date ||
              !value ||
              new Date(value) > new Date(project_start_date)
            );
          }
        ),
      project_owner: Yup.string()
        .trim()
        .required("Project Owner name is required"),
      project_contact_name: Yup.string()
        .trim()
        .required("Project contact person name is required"),
      project_contact_email: Yup.string()
        .trim()
        .required("Project contact person Email is required")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Please enter a valid email address."
        ),
      project_contact_phone: Yup.string()
        .trim()
        .matches(/^[6-9]\d{9}$/, {
          message: "Please enter valid number.",
          excludeEmptyString: false,
        })
        .required("Project contact person phone is required"),
      project_po_ref: Yup.string()
        .trim()
        .required("Project PO Reference is required"),
      project_po_date: Yup.string()
        .trim()
        .required("Project PO Date is required"),
      project_logo: Yup.string().required("Project Logo is required"),
      project_impressions_target: Yup.number()
        .required("Project Impressions Target is required")
        .moreThan(0, "Please enter valid Impression target."),
    }),
    onSubmit: async (values) => {
      const payload = { ...values };
      payload.project_status === 0
        ? (payload.project_status = 2)
        : (payload.project_status = 1);

      payload.project_attributes = [
        {
          key: "tags",
          value: JSON.stringify(createdTags),
        },
        {
          key: "show_speciality_info",
          value: JSON.stringify(values.show_speciality_info),
        },
      ];

      const mappedLocationData = getMappedData(dropdownGroups);
      const mappedSpecialityData = getMappedData(specialitiesGroups);

      if (!isEmpty(mappedLocationData)) {
        payload.project_attributes.push({
          key: "mapped_location_data",
          value: JSON.stringify(mappedLocationData),
        });
      }
      if (!isEmpty(mappedSpecialityData)) {
        payload.project_attributes.push({
          key: "mapped_speciality_data",
          value: JSON.stringify(mappedSpecialityData),
        });
      }

      if (
        !payload?.project_to_channel?.length ||
        payload?.project_to_channel?.some(
          (item) => !item.channel_id || !item.type_label
        )
      ) {
        return toast.error("Please select at least one channel");
      }

      try {
        showBackdrop();
        delete payload?.dbRef;
        delete payload?.show_speciality_info;
        let response;
        if (projectDetails.length > 0) {
          response = await postService(UPDATE_PROJECT, payload);
        } else {
          response = await postService(ADD_PROJECT, payload);
        }

        if (response?.data?.success) {
          queryClient.invalidateQueries({
            queryKey: [GET_PROJECT_POST_LIST],
          });
          toast.success("Project created successfully");
          redirectTo("workspace/clirnet");
        } else {
          toast.error("Something went wrong");
        }
      } catch (e) {
        console.log(e);
        toast.error("Something went wrong");
      } finally {
        hideBackdrop();
      }
    },
  });

  const {
    values,
    touched,
    errors,
    handleSubmit,
    handleChange,
    setFieldValue,
    setValues,
  } = formik;

  useEffect(() => {
    if (projectDetails.length > 0) {
      setIsEditProject(true);
      preFillProjectForm();
    }
  }, []);

  function preFillProjectForm() {
    const projectImpressionsTarget =
      projectDetails?.at(0)?.project_target?.impressions_target || 0;
    const projectStatus = projectDetails?.at(0)?.project_status === 2 ? 0 : 1;

    const projectToChannel = projectDetails
      ?.at(0)
      ?.project_to_channel?.map((item) => {
        return {
          type_label: item.type_label,
          channel_id: item.channel_id,
          target_views: item.target_views,
          target_impression: item.target_impression,
          target_engagement: item.target_engagement,
        };
      });

    const projectTags = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "tags"
      )?.attribute_value;
    const showSpecialityInfo = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "show_speciality_info"
      )?.attribute_value;
    const stateMappedAttribute = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item.attribute_name === "mapped_location_data"
      )?.attribute_value;
    const specialityMappedAttribute = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) =>
          item?.attribute_name === "mapped_speciality_data" &&
          item?.attribute_value !== "true" &&
          item?.attribute_value !== "false"
      )?.attribute_value;

    const projectTagsArray = projectTags ? JSON.parse(projectTags) : [];
    const showSpecialityInfoData = showSpecialityInfo
      ? JSON.parse(showSpecialityInfo)
      : 1;

    if (!!stateMappedAttribute) {
      const mappedStatesData = JSON.parse(stateMappedAttribute);
      const transformedData = Object.keys(mappedStatesData).map(
        (key, index) => ({
          id: index + 1,
          firstSelect: key,
          secondSelect: mappedStatesData[key],
          isLocked: true,
        })
      );
      transformedData.push({
        id: transformedData.length + 1,
        firstSelect: "",
        secondSelect: "",
        isLocked: false,
      });
      setDropdownGroups(transformedData);
    }

    if (!!specialityMappedAttribute) {
      const mappedSpecialityData = JSON.parse(specialityMappedAttribute);
      const transformedData = Object.keys(mappedSpecialityData).map(
        (key, index) => ({
          id: index + 1,
          firstSelect: key,
          secondSelect: mappedSpecialityData[key],
          isLocked: true,
        })
      );
      transformedData.push({
        id: transformedData.length + 1,
        firstSelect: "",
        secondSelect: "",
        isLocked: false,
      });
      setSpecialitiesGroups(transformedData);
    }

    setCreatedTags(projectTagsArray);
    setValues({
      ...values,
      ...projectDetails?.at(0),
      project_status: projectStatus,
      show_speciality_info: showSpecialityInfoData,
      project_impressions_target: projectImpressionsTarget,
      project_to_channel:
        projectToChannel?.length > 0
          ? projectToChannel
          : [
              {
                type_label: "",
                channel_id: "",
                target_views: "",
                target_impression: "",
                target_engagement: "",
              },
            ],
    });
  }

  const onTagsChangeHandler = (selectedValue) => {
    setCreatedTags(selectedValue);
  };

  const fields = [
    {
      row: 1,
      columns: [
        {
          id: 1,
          label: "Project Name",
          field_name: "project_name",
          type: "text",
          placeholder: "Enter Project Name",
          required: true,
          colSize: 6,
        },
        {
          id: 2,
          label: "Principal Entity",
          field_name: "principal_entity_id",
          type: "select",
          options: getPrincipleEntitiesForSelect(
            createProjectData?.principal_entities || []
          ),
          required: true,
          optionLabelKey: "principal_entity_name",
          optionValueKey: "principal_entity_id",
          placeholder: "Select Principal Entity",
          colSize: 3,
        },
        {
          id: 3,
          label: "Project Status",
          field_name: "project_status",
          type: "switch",
          required: false,
          disabled: !isEditProject,
          colSize: 3,
        },
      ],
    },
    {
      row: 2,
      columns: [
        {
          id: 1,
          label: "Project Description",
          field_name: "project_desc",
          type: "textarea",
          placeholder: "Enter Project Description",
          required: true,
        },
        {
          id: 2,
          label: "Project Logo",
          field_name: "project_logo",
          type: "file",
          required: true,
        },
      ],
    },
    {
      row: 3,
      columns: [
        {
          id: 1,
          label: "Project Owner Name",
          field_name: "project_owner",
          type: "text",
          placeholder: "Enter Project Owner",
          required: true,
        },
        {
          id: 2,
          label: "Project Start Date",
          field_name: "project_start_date",
          type: "date",
          required: true,
        },
        {
          id: 3,
          label: "Project End Date",
          field_name: "project_end_date",
          type: "date",
          required: true,
        },
      ],
    },
    {
      row: 4,
      columns: [
        {
          id: 1,
          label: "Project Contact Person Name",
          field_name: "project_contact_name",
          type: "text",
          placeholder: "Enter Owner Contact",
          required: true,
        },
        {
          id: 2,
          label: "Project Contant Person Email",
          field_name: "project_contact_email",
          type: "text",
          placeholder: "Enter Owner Email",
          required: true,
        },
        {
          id: 3,
          label: "Project Contact Person Phone",
          field_name: "project_contact_phone",
          type: "number",
          placeholder: "Enter Owner Phone",
          required: true,
        },
      ],
    },
    {
      row: 5,
      columns: [
        {
          id: 1,
          label: "Purchase Order Reference",
          field_name: "project_po_ref",
          type: "text",
          placeholder: "Enter Purchase Order Reference",
          required: true,
        },
        {
          id: 2,
          label: "Purchase Order Date",
          field_name: "project_po_date",
          type: "date",
          placeholder: "Enter Purchase Order Date",
          required: true,
        },
        {
          id: 3,
          label: "Project Impressions Target",
          field_name: "project_impressions_target",
          placeholder: "Enter Project Impressions Target",
          type: "number",
          required: true,
        },
      ],
    },
    {
      row: 6,
      columns: [
        {
          id: 1,
          label: "Show Speciality Info",
          field_name: "show_speciality_info",
          type: "switch",
          required: false,
        },
      ],
    },
    {
      row: 7,
      columns: [
        {
          id: 1,
          label: "Project Tags",
          field_name: "tags",
          type: "creatableSelect",
          required: false,
          options: createdTags,
          onCustomChange: onTagsChangeHandler,
        },
      ],
    },
  ];

  const _loadSuggestions = (inputValue) => {
    const searchValue = isNaN(inputValue) ? inputValue : Number(inputValue);
    return new Promise(async (resolve) => {
      const response = await postService(GET_CONTENT_SEARCH_DATA, {
        search_key: searchValue,
        search_type: "channel",
      });
      if (!response.isError) {
        const searchData = [];
        response?.data?.data &&
          response?.data?.data.map((elem) => {
            searchData.push({
              value: elem.id,
              label: `${elem.id} - ${elem.title}`,
            });
          });

        resolve(searchData);
      } else {
        toast.error("Error fetching data!");
      }
    });
  };

  return (
    <div>
      <Form onSubmit={handleSubmit}>
        <FormBuilder
          fields={fields}
          handleChange={handleChange}
          values={values}
          touched={touched}
          errors={errors}
          setFieldValue={setFieldValue}
        />

        <div>
          <p className="form-label">Choose Channels</p>
          <FormikProvider value={formik}>
            <FieldArray name="project_to_channel">
              {(prop) => (
                <>
                  {values.project_to_channel?.map((item, index) => (
                    <div
                      key={index}
                      className="border border-1 border-primary border-opacity-25 border-dashed rounded-3 mb-4 p-4 px-3"
                    >
                      <Row>
                        <Col lg={12}>
                          <RenderField
                            required={true}
                            type="searchable"
                            placeholder="Search Channel"
                            label="Search Channel *"
                            field_name={`project_to_channel[${index}].channel_id`}
                            searchText={searchText}
                            loadOptions={debounce((inputText, callback) => {
                              setSearchText(inputText);
                              _loadSuggestions(inputText)
                                .then((options) => {
                                  callback(options);
                                })
                                .catch((err) => console.log(err));
                            }, 300)}
                            handleChange={(val) => {
                              if (val) {
                                setFieldValue(
                                  `project_to_channel[${index}].channel_id`,
                                  val?.value || ""
                                );
                                setFieldValue(
                                  `project_to_channel[${index}].type_label`,
                                  val?.label || ""
                                );
                              } else {
                                setFieldValue(
                                  `project_to_channel[${index}].channel_id`,
                                  ""
                                );
                                setFieldValue(
                                  `project_to_channel[${index}].type_label`,
                                  ""
                                );
                              }
                            }}
                            selected={
                              values.project_to_channel[index]?.type_label
                                ? {
                                    value:
                                      values.project_to_channel[index]
                                        ?.channel_id,
                                    label:
                                      values.project_to_channel[index]
                                        ?.type_label,
                                  }
                                : ""
                            }
                            values={values}
                            touched={touched}
                            errors={errors}
                            setFieldValue={setFieldValue}
                          />
                        </Col>

                        <Col lg={4}>
                          <RenderField
                            type="number"
                            field_name={`project_to_channel[${index}].target_views`}
                            placeholder="Target Views"
                            values={values}
                            touched={touched}
                            errors={errors}
                            handleChange={handleChange}
                            label="Target Views"
                            required={true}
                          />
                        </Col>
                        <Col lg={4}>
                          <RenderField
                            type="number"
                            field_name={`project_to_channel[${index}].target_impression`}
                            placeholder="Target Impression"
                            values={values}
                            touched={touched}
                            errors={errors}
                            handleChange={handleChange}
                            label="Target Impression"
                            required={true}
                          />
                        </Col>
                        <Col lg={4}>
                          <RenderField
                            type="number"
                            field_name={`project_to_channel[${index}].target_engagement`}
                            placeholder="Target Engagement"
                            values={values}
                            touched={touched}
                            errors={errors}
                            handleChange={handleChange}
                            label="Target Engagement"
                            required={true}
                          />
                        </Col>

                        <Col
                          lg={12}
                          className="d-flex justify-content-center align-items-center gap-2 flex-wrap"
                        >
                          <Button
                            variant="outline-danger"
                            className="border-dashed flex-shrink-0 flex-sm-grow-0 flex-grow-1 px-md-4 px-3"
                            onClick={() => prop?.remove(index)}
                            disabled={
                              index == 0 &&
                              values.project_to_channel?.length < 2
                                ? true
                                : false
                            }
                          >
                            <span>{findIcon("Trash", "dual-tone", 22)}</span>{" "}
                            Remove
                          </Button>

                          {values.project_to_channel?.length - 1 === index && (
                            <Button
                              variant={`${
                                values[`project_to_channel`][index]
                                  ?.type_label == ""
                                  ? "outline-secondary"
                                  : "outline-primary"
                              }`}
                              className="border-dashed flex-shrink-0 flex-sm-grow-0 flex-grow-1 px-4 px-3"
                              onClick={() =>
                                prop?.push({
                                  type_label: "",
                                  channel_id: "",
                                  target_views: "",
                                  target_impression: "",
                                  target_engagement: "",
                                })
                              }
                              disabled={
                                values[`project_to_channel`][index]
                                  ?.type_label == ""
                                  ? true
                                  : false
                              }
                            >
                              <span>{findIcon("Plus", "solid", 22)}</span> Add
                              Row
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </div>
                  ))}
                </>
              )}
            </FieldArray>
          </FormikProvider>
        </div>

        <div>
          <p>Mapped States</p>
          <MappedStates
            stateLists={stateLists}
            dropdownGroups={dropdownGroups}
            setDropdownGroups={setDropdownGroups}
          />
        </div>
        <div>
          <p>Mapped Specialities</p>
          <MappedSpecialities
            masterSpecialities={masterSpecialities}
            specialitiesGroups={specialitiesGroups}
            setSpecialitiesGroups={setSpecialitiesGroups}
          />
        </div>

        <div className="d-flex justify-content-end gap-3">
          <Button
            variant="secondary"
            onClick={() => redirectTo("workspace/clirnet")}
          >
            Cancel
          </Button>
          <Button type="submit">{`${
            projectDetails.length > 0 ? "Update" : "Submit"
          }`}</Button>
        </div>
      </Form>
    </div>
  );
};

export default SponsoredCommunityForm;
